import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase/config.js';
import {
  collection,
  writeBatch,
  doc,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  deleteField,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import Papa from 'papaparse';
import { format, parseISO, isValid as isValidDate, isBefore, isAfter } from 'date-fns';
import { getAuth } from 'firebase/auth';
import TenantConfigService from '../services/TenantConfigService.js';

// --- DEBUG FUNCTIONS ---
const debugTokenClaims = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (user) {
    try {
      const idTokenResult = await user.getIdTokenResult();
      console.log('🔍 Token Claims Debug:', {
        uid: user.uid,
        email: user.email,
        claims: idTokenResult.claims
      });
      
      // Check specific claims your rules need
      console.log('📋 Required Claims:', {
        tenantId: idTokenResult.claims.tenantId,
        role: idTokenResult.claims.role,
        firestoreDocId: idTokenResult.claims.firestoreDocId,
        isSuperAdmin: idTokenResult.claims.isSuperAdmin,
        hasElevatedPrivileges: idTokenResult.claims.hasElevatedPrivileges
      });
      
      return idTokenResult.claims;
    } catch (error) {
      console.error('❌ Error getting token claims:', error);
    }
  } else {
    console.error('❌ No authenticated user found');
  }
};

// Add this to debug the document data you're sending
const debugDocumentData = (userData) => {
  console.log('📄 Document Data Being Sent:', userData);
  console.log('🏢 Tenant ID in document:', userData.tenantId);
};

// --- SubRequestEditForm Component ---
const SubRequestEditForm = ({ request, onSave, onCancel, isSaving, facultyList = [] }) => {
    const [formData, setFormData] = useState({
        date: '',
        periods: '',
        reason: '',
        room: '',
        periodNotes: {},
        filled: {},
    });

    useEffect(() => {
        if (request) {
            let dateStr = '';
            if (request.date) {
               if (request.date.toDate) {
                   dateStr = format(request.date.toDate(), 'yyyy-MM-dd');
               } else if (typeof request.date === 'string') {
                   const parsed = parseISO(request.date);
                   dateStr = isValidDate(parsed) ? format(parsed, 'yyyy-MM-dd') : request.date;
               }
            }

            setFormData({
                date: dateStr,
                periods: (request.periods || []).join(', '),
                reason: request.reason || '',
                room: request.room || '',
                periodNotes: request.periodNotes || {},
                filled: request.filled || {},
            });
        }
    }, [request]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleNoteChange = (period, value) => {
        setFormData(prev => ({
            ...prev,
            periodNotes: { ...prev.periodNotes, [period]: value },
        }));
    };

    const handleFilledChange = (period, selectedSisId) => {
        setFormData(prev => {
            const newFilled = { ...prev.filled };
            if (!selectedSisId) {
                delete newFilled[period];
            } else {
                const selectedUser = facultyList.find(user => user.sis_id === selectedSisId);
                if (selectedUser) {
                    newFilled[period] = {
                        name: selectedUser.name,
                        email: selectedUser.email,
                        sis_id: selectedUser.sis_id,
                        uid: selectedUser.docId,
                    };
                }
            }
            return { ...prev, filled: newFilled };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const periodsArray = formData.periods
            .split(',')
            .map(p => p.trim())
            .filter(p => p && !isNaN(p));

        if (!formData.date || !isValidDate(parseISO(formData.date))) {
            alert('Please enter a valid date in yyyy-MM-dd format.'); return;
        }
        if (periodsArray.length === 0) {
            alert('Please enter at least one valid period number, separated by commas.'); return;
        }

        const finalPeriodNotes = {};
        const finalFilled = {};
        periodsArray.forEach(p => {
            finalPeriodNotes[p] = formData.periodNotes[p] || '';
            if (formData.filled[p]) {
                finalFilled[p] = formData.filled[p];
            }
        });

        const updatedData = {
            date: formData.date,
            periods: periodsArray,
            reason: formData.reason,
            room: formData.room,
            periodNotes: finalPeriodNotes,
            filled: finalFilled,
        };
        onSave(request.docId, updatedData);
    };

    const currentPeriods = formData.periods.split(',').map(p => p.trim()).filter(p => p && !isNaN(p));

    if (!request) return null;

    return (
        <div style={{ border: '1px solid #ddd', padding: '25px', marginBottom: '25px', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
            <h3 style={{ marginTop: 0, color: '#1976d2', fontSize: '20px' }}>✏️ Edit Sub Request for {request.name}</h3>
            <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                        <label htmlFor="edit_date" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Date (YYYY-MM-DD):</label>
                        <input type="date" id="edit_date" name="date" value={formData.date} onChange={handleChange} required 
                               style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                        <label htmlFor="edit_periods" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Periods (comma-separated):</label>
                        <input type="text" id="edit_periods" name="periods" value={formData.periods} onChange={handleChange} required 
                               style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                               placeholder="e.g., 1, 3, 5" />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                        <label htmlFor="edit_reason" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Reason:</label>
                        <input type="text" id="edit_reason" name="reason" value={formData.reason} onChange={handleChange} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                    <div>
                        <label htmlFor="edit_room" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Room:</label>
                        <input type="text" id="edit_room" name="room" value={formData.room} onChange={handleChange} 
                               style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }} />
                    </div>
                </div>
                {currentPeriods.length > 0 && (
                    <div style={{ marginTop: '25px', padding: '20px', border: '1px solid #e3f2fd', borderRadius: '8px', backgroundColor: '#fafafa' }}>
                        <h4 style={{ marginTop: 0, color: '#1976d2', fontSize: '16px' }}>Period Details:</h4>
                        {currentPeriods.map(period => (
                            <div key={`details_${period}`} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '20px', marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #eee' }}>
                                <div>
                                    <label htmlFor={`edit_filled_${period}`} style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>P{period} Filled By:</label>
                                    <select
                                        id={`edit_filled_${period}`}
                                        value={formData.filled[period]?.sis_id || ''}
                                        onChange={(e) => handleFilledChange(period, e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                                    >
                                        <option value="">-- Needed --</option>
                                        <optgroup label="Faculty/Staff">
                                            {facultyList
                                                .filter(user => user.role !== 'Outside Sub')
                                                .map(user => (
                                                    <option key={user.docId} value={user.sis_id}>
                                                        {user.name} ({user.email})
                                                    </option>
                                                ))
                                            }
                                        </optgroup>
                                        <optgroup label="Outside Substitutes">
                                            {facultyList
                                                .filter(user => user.role === 'Outside Sub')
                                                .map(user => (
                                                    <option key={user.docId} value={user.sis_id}>
                                                        {user.name} ({user.email})
                                                    </option>
                                                ))
                                            }
                                        </optgroup>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor={`edit_note_${period}`} style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Note for P{period}:</label>
                                    <input
                                        type="text"
                                        id={`edit_note_${period}`}
                                        value={formData.periodNotes[period] || ''}
                                        onChange={(e) => handleNoteChange(period, e.target.value)}
                                        style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box' }}
                                        placeholder={`Enter notes for P${period}...`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div style={{ marginTop: '25px', display: 'flex', gap: '15px' }}>
                    <button type="submit" disabled={isSaving} 
                            style={{ padding: '12px 20px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button type="button" onClick={onCancel} disabled={isSaving} 
                            style={{ padding: '12px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};


const Admin = (props) => {
  const { currentUser } = props;

  const usersSectionRef = useRef(null);
  const requestsSectionRef = useRef(null);
  const userEditFormRef = useRef(null); 
  const [csvFile, setCsvFile] = useState(null);
  const [isProcessingCSV, setIsProcessingCSV] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [errorMessages, setErrorMessages] = useState([]);
  const [allFaculty, setAllFaculty] = useState([]);
  const [filteredFacultyList, setFilteredFacultyList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userViewFilter, setUserViewFilter] = useState('active');
  const [userForm, setUserForm] = useState({
    docId: null, firstName: '', lastName: '', email: '', role: '', department: '',
    secondaryDepartment: '', // Secondary department for dual-department teachers
    division: 'LS', // NEW: Add division with default
    sis_id: '', phone: '', title: '', supervisorId: '', status: 'active',
    secondaryRoles: [] // NEW: Add secondary roles array
  });
  const [showUserForm, setShowUserForm] = useState(false);
  const [formMode, setFormMode] = useState('add');
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [allSubRequests, setAllSubRequests] = useState([]);
  const [filteredSubRequests, setFilteredSubRequests] = useState([]);
  const [isLoadingSubRequests, setIsLoadingSubRequests] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [subRequestFilters, setSubRequestFilters] = useState({
    dateFrom: '', dateTo: '', teacherName: '', department: '', status: 'all',
  });
  const [editingRequest, setEditingRequest] = useState(null);
  const TEMPLATE_FILE_NAME = "faculty_staff_template_v5.csv"; // Updated version

  // Available roles for dropdown
  const availableRoles = [
    'Faculty/Staff',
    'HR',
    'System Administrator',
    'Educational Administrator',
    'Outside Sub',
    'Volunteer Manager',
    'Volunteer'
  ];

  // Available academic departments for secondary department dropdown
  const availableAcademicDepartments = [
    'English',
    'Math', 
    'Science',
    'History',
    'World Languages',
    'PE',
    'Arts',
    'Visual Arts',
    'Performing Arts',
    'Religion',
    'Wellness'
  ].sort();

  // Secondary roles management functions
  const addSecondaryRole = (role) => {
    if (!userForm.secondaryRoles.includes(role) && role !== userForm.role) {
      setUserForm(prev => ({
        ...prev,
        secondaryRoles: [...prev.secondaryRoles, role]
      }));
    }
  };

  const removeSecondaryRole = (role) => {
    setUserForm(prev => ({
      ...prev,
      secondaryRoles: prev.secondaryRoles.filter(r => r !== role)
    }));
  };

  const handleFileChange = (event) => {
    setCsvFile(event.target.files[0]);
    setUploadStatus(''); setProcessedCount(0); setErrorCount(0); setErrorMessages([]);
  };

  const handleDownloadTemplate = () => {
    // UPDATED: Add mobileNumber to CSV template
    const csvHeader = "sis_id,firstName,lastName,email,role,department,secondaryDepartment,division,mobileNumber,title,status,supervisor_sis_id\n";
    const csvExample = "101,John,Smith,john.smith@example.com,Faculty/Staff,Science,,MS,5551234567,Science Teacher,active,201\n";
    const csvContent = csvHeader + csvExample;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', TEMPLATE_FILE_NAME);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  const scrollToRef = (ref) => {
      if (ref.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
  };

  // Sync Sub Jobs Function
  const handleSyncSubJobs = async () => {
    if (!window.confirm('This will create sub_jobs records for all filled positions that don\'t have them. Continue?')) {
      return;
    }
    
    setIsSavingUser(true);
    setUploadStatus('Syncing sub_jobs records...');
    
    try {
      const subRequestsQuery = query(
        collection(db, 'sub_requests'),
        where('tenantId', '==', currentUser.tenantId)
      );
      
      const requestsSnapshot = await getDocs(subRequestsQuery);
      
      const existingJobsQuery = query(
        collection(db, 'sub_jobs'),
        where('tenantId', '==', currentUser.tenantId)
      );
      const existingJobsSnapshot = await getDocs(existingJobsQuery);
      const existingJobs = new Set();
      existingJobsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const key = `${data.subRequestId}-${data.period}`;
        existingJobs.add(key);
      });
      
      const facultyQuery = query(
        collection(db, 'faculty_staff'),
        where('tenantId', '==', currentUser.tenantId),
        where('role', '==', 'Outside Sub')
      );
      const facultySnapshot = await getDocs(facultyQuery);
      const facultyMap = new Map();
      facultySnapshot.docs.forEach(doc => {
        const data = doc.data();
        facultyMap.set(data.email, { id: doc.id, ...data });
      });
      
      let createdCount = 0;
      const batch = writeBatch(db);
      
      for (const requestDoc of requestsSnapshot.docs) {
        const requestData = requestDoc.data();
        const requestId = requestDoc.id;
        
        let requestDate;
        if (requestData.date?.toDate) {
          requestDate = requestData.date.toDate();
        } else if (typeof requestData.date === 'string') {
          requestDate = new Date(requestData.date);
        }
        
        if (!requestDate) continue;
        
        if (requestData.filled && Object.keys(requestData.filled).length > 0) {
          for (const [period, filledInfo] of Object.entries(requestData.filled)) {
            const jobKey = `${requestId}-${period}`;
            
            if (existingJobs.has(jobKey)) continue;
            
            const substitute = facultyMap.get(filledInfo.email);
            if (!substitute) continue;
            
            const subJobData = {
              substituteId: substitute.id,
              tenantId: currentUser.tenantId,
              jobDate: requestDate,
              description: `${requestData.name} - ${requestData.department} - Period ${period}`,
              hoursWorked: 1,
              payAmount: parseFloat(substitute.payRate || substitute.dailyRate || 150),
              status: 'Paid',
              createdAt: serverTimestamp(),
              subRequestId: requestId,
              period: period,
              requesterName: requestData.name,
              department: requestData.department
            };
            
            const newJobRef = doc(collection(db, 'sub_jobs'));
            batch.set(newJobRef, subJobData);
            createdCount++;
            
            if (createdCount % 400 === 0) {
              await batch.commit();
              const newBatch = writeBatch(db);
              Object.assign(batch, newBatch);
            }
          }
        }
      }
      
      if (createdCount % 400 !== 0) {
        await batch.commit();
      }
      
      if (createdCount > 0) {
        setUploadStatus(`✅ Successfully created ${createdCount} sub_jobs records!`);
      } else {
        setUploadStatus('No new sub_jobs records needed - all filled positions already have job records.');
      }
      
    } catch (error) {
      console.error('Error syncing sub jobs:', error);
      setUploadStatus(`Error syncing sub jobs: ${error.message}`);
    } finally {
      setIsSavingUser(false);
    }
  };
  
  // CSV Upload Function
  const handleUpload = async () => {
    if (!csvFile) { alert("Please select a CSV file."); return; }
    if (!currentUser?.isSystemAdmin) { alert("CSV upload restricted to System Administrators."); return; }
    
    setIsProcessingCSV(true);
    setUploadStatus('Preparing for upload...');
    setErrorMessages([]);
    setErrorCount(0);
    setProcessedCount(0);

    const supervisorMap = new Map();
    try {
        setUploadStatus('Fetching existing user data for validation...');
        const facultyQuery = query(collection(db, 'faculty_staff'), where('tenantId', '==', currentUser.tenantId));
        const snapshot = await getDocs(facultyQuery);
        snapshot.forEach(doc => {
            const data = doc.data();
            if (data.sis_id) {
                supervisorMap.set(data.sis_id, doc.id);
            }
        });
    } catch (error) {
        console.error("Error creating supervisor map:", error);
        setUploadStatus(`Error fetching existing users: ${error.message}`);
        setIsProcessingCSV(false);
        return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let localProcessed = 0;
        let localErrors = 0;
        const localErrorMsgsArr = [];
        
        const dataRows = results.data;
        if (results.errors.length) {
            results.errors.forEach(err => localErrorMsgsArr.push(`CSV Row Parsing Error: ${err.message} (Row: ${err.row + 2})`));
            localErrors += results.errors.length;
        }

        dataRows.forEach(row => {
            const sis_id = row.sis_id?.trim();
            if (sis_id && !supervisorMap.has(sis_id)) {
                supervisorMap.set(sis_id, `${currentUser.tenantId}_${sis_id}`);
            }
        });

        setUploadStatus(`CSV parsed. Found ${dataRows.length} users. Writing to database...`);
        const batchSize = 400;

        for (let i = 0; i < dataRows.length; i += batchSize) {
          const batch = writeBatch(db);
          const chunk = dataRows.slice(i, i + batchSize);
          let validRowsInChunk = 0;

          for (const row of chunk) {
            const sis_id = row.sis_id?.trim();
            const firstName = row.firstName?.trim();
            const lastName = row.lastName?.trim();
            const email = row.email?.trim().toLowerCase();
            const role = row.role?.trim();
            const division = row.division?.trim(); // Required field
            const supervisor_sis_id = row.supervisor_sis_id?.trim();

            // Check for missing required fields
            if (!sis_id || !firstName || !lastName || !email || !role || !division) {
              const missing = ["sis_id", "firstName", "lastName", "email", "role", "division"].filter(f => !row[f]?.trim()).join(", ");
              localErrorMsgsArr.push(`Row Skipped (SIS ID: ${sis_id || 'N/A'}): Missing required fields: ${missing}.`);
              localErrors++;
              continue;
            }

            // Validate division values - use dynamic divisions from tenant config
            const validDivisionCodes = divisions.map(d => d.code);
            if (!validDivisionCodes.includes(division)) {
              localErrorMsgsArr.push(`Row Skipped (SIS ID: ${sis_id}): Invalid division '${division}'. Must be one of: ${validDivisionCodes.join(', ')}.`);
              localErrors++;
              continue;
            }

            // Validate supervisor if provided
            let supervisorDocId = null;
            if (supervisor_sis_id && supervisor_sis_id !== '') {
              if (supervisorMap.has(supervisor_sis_id)) {
                supervisorDocId = supervisorMap.get(supervisor_sis_id);
              } else {
                localErrorMsgsArr.push(`Row Skipped (SIS ID: ${sis_id}): Supervisor with SIS ID '${supervisor_sis_id}' not found.`);
                localErrors++;
                continue;
              }
            }

            const facultyDataForFirestore = {
              firstName,
              lastName,
              name: `${firstName} ${lastName}`,
              email,
              role,
              department: row.department?.trim() || '',
              secondaryDepartment: row.secondaryDepartment?.trim() || '',
              division: division, // Now guaranteed to be valid
              sis_id,
              title: row.title?.trim() || '',
              phone: row.phone?.trim() || '',
              status: row.status?.trim().toLowerCase() || 'active',
              tenantId: currentUser.tenantId,
              supervisorId: supervisorDocId,
              secondaryRoles: [] // Initialize empty secondary roles array
            };
            
            const compositeId = `${currentUser.tenantId}_${sis_id}`;
            const docRef = doc(db, "faculty_staff", compositeId);
            batch.set(docRef, facultyDataForFirestore, { merge: true });
            localProcessed++;
            validRowsInChunk++;
          }

          try {
            if (validRowsInChunk > 0) {
              await batch.commit();
            }
          } catch (batchError) {
            console.error(`Error committing batch:`, batchError);
            localErrorMsgsArr.push(`Critical Error committing batch: ${batchError.message}`);
            localErrors += validRowsInChunk;
            localProcessed -= validRowsInChunk;
          }
        }

        setProcessedCount(localProcessed); 
        setErrorCount(localErrors); 
        setErrorMessages(localErrorMsgsArr);
        if (localErrors > 0) setUploadStatus(`Upload finished. Processed: ${localProcessed}. Errors: ${localErrors}.`);
        else setUploadStatus(`Upload successful! ${localProcessed} users created/updated.`);
        
        setIsProcessingCSV(false); 
        setCsvFile(null);
        fetchFacultyList();
      },
      error: (err) => {
        console.error("PapaParse File Reading Error:", err);
        setUploadStatus(`Error parsing CSV: ${err.message}`);
        setIsProcessingCSV(false);
      }
    });
  };

  const fetchFacultyList = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      let q = collection(db, "faculty_staff");
      if (currentUser && currentUser.tenantId) {
          q = query(q, where("tenantId", "==", currentUser.tenantId));
      } else if (currentUser?.isSystemAdmin && !currentUser.tenantId) {
        console.warn("Admin.js - fetchFacultyList: No tenantId, fetching all faculty.");
      } else if (!currentUser) {
          setIsLoadingUsers(false);
          return;
      }
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(d => ({ docId: d.id, ...d.data() }));
      users.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAllFaculty(users);
    } catch (error) {
      console.error("Admin.js - Error fetching faculty list:", error);
      setUploadStatus(`Error fetching user list: ${error.message}`);
    }
    setIsLoadingUsers(false);
  }, [currentUser]);

  useEffect(() => {
    if (userViewFilter === 'all') setFilteredFacultyList(allFaculty);
    else setFilteredFacultyList(allFaculty.filter(user => (user.status || 'active') === userViewFilter));
  }, [allFaculty, userViewFilter]);

  useEffect(() => {
    if (currentUser?.isSystemAdmin || currentUser?.isEducationalAdmin) {
        fetchFacultyList();
    }
  }, [currentUser, fetchFacultyList]);

  useEffect(() => {
  const loadDivisions = async () => {
    if (!currentUser?.tenantId) return;
    
    try {
      const divisionsConfig = await TenantConfigService.getConfig(
        currentUser.tenantId, 
        'divisions',
        {
          divisions: [
            { code: 'EC', label: 'Early Childhood', color: '#007bff', order: 1 },
            { code: 'LS', label: 'Lower School', color: '#28a745', order: 2 },
            { code: 'MS', label: 'Middle School', color: '#ffc107', order: 3 },
            { code: 'HS', label: 'High School', color: '#dc3545', order: 4 }
          ]
        }
      );
      
      if (divisionsConfig.divisions) {
  setDivisions(divisionsConfig.divisions);
}
    } catch (error) {
      console.error('Error loading divisions:', error);
    }
  };
  
  loadDivisions();
}, [currentUser]);

  const handleUserFormChange = (e) => setUserForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  
  const openAddUserForm = () => {
      setShowUserForm(true); setFormMode('add');
      setUserForm({ 
        docId: null, firstName: '', lastName: '', email: '', role: '', department: '',
        secondaryDepartment: '',
        division: 'LS', // NEW: Add division default
        sis_id: '', phone: '', title: '', supervisorId: '', status: 'active',
        secondaryRoles: []
      });
      setUploadStatus(''); setErrorMessages([]);
      setTimeout(() => scrollToRef(userEditFormRef), 50);
  };
  
  const openEditUserForm = (user) => {
    setShowUserForm(true); setFormMode('edit');
    setUserForm({ 
      ...user, 
      status: user.status || 'active',
      division: user.division || 'LS',
      secondaryRoles: user.secondaryRoles || [],
      mobileNumber: user.mobileNumber || user.phone || '' // Fallback to legacy phone
    });
    setUploadStatus(''); setErrorMessages([]);
    setTimeout(() => scrollToRef(userEditFormRef), 50);
};

  const handleUserFormSubmit = async (e) => {
    e.preventDefault();
    setIsSavingUser(true);
    setUploadStatus('');
    setErrorMessages([]);

    console.log('🚀 Starting user creation process...');
    const claims = await debugTokenClaims();

    if (!currentUser || !currentUser.tenantId) {
        alert("Critical Error: Your tenant information is missing. Cannot save user.");
        setIsSavingUser(false);
        return;
    }

    const { docId, firstName, lastName, email, role, department, secondaryDepartment, division, sis_id, phone, title, supervisorId, status, secondaryRoles } = userForm;
    
    let finalDepartment = department?.trim() || '';
    const trimmedRole = role?.trim();
    if (trimmedRole === "Outside Sub") finalDepartment = "Outside Sub";
    const finalSisId = sis_id?.trim();
    const finalDivision = division || 'LS'; // NEW: Handle division

    if (!finalSisId || !firstName?.trim() || !lastName?.trim() || !email?.trim() || !trimmedRole || !finalDepartment) {
      alert("Required fields are missing: SIS ID, First/Last Name, Email, Role, Department.");
      setIsSavingUser(false);
      return;
    }
    
    const combinedName = `${firstName.trim()} ${lastName.trim()}`;
    const userData = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      name: combinedName,
      email: email.trim().toLowerCase(),
      role: trimmedRole,
      department: finalDepartment,
      secondaryDepartment: secondaryDepartment?.trim() || '',
      division: finalDivision, // NEW: Add division
      sis_id: finalSisId,
      phone: phone?.trim() || '',
      title: title?.trim() || '',
      supervisorId: supervisorId || null,
      status: status || 'active',
      tenantId: currentUser.tenantId,
      secondaryRoles: secondaryRoles || []
    };

    debugDocumentData(userData);

    if (claims && userData.tenantId) {
      console.log('🔍 Tenant ID Match Check:', {
        tokenTenantId: claims.tenantId,
        documentTenantId: userData.tenantId,
        match: claims.tenantId === userData.tenantId
      });
    }

    try {
      if (formMode === 'add') {
        const compositeId = `${currentUser.tenantId}_${finalSisId}`;
        const newUserDocRef = doc(db, "faculty_staff", compositeId);
        const docSnap = await getDoc(newUserDocRef);
        if (docSnap.exists()) { 
          alert(`Error: A user with SIS ID "${finalSisId}" already exists in this tenant.`);
          setIsSavingUser(false); 
          return;
        }
        
        console.log('📝 Attempting to create user with document ID:', compositeId);
        await setDoc(newUserDocRef, userData);
        console.log('✅ User created successfully!');
        setUploadStatus(`User "${combinedName}" added successfully.`);
      } else if (formMode === 'edit') {
        const originalDocRef = doc(db, "faculty_staff", docId);
        await updateDoc(originalDocRef, userData); 
        setUploadStatus(`User "${combinedName}" updated successfully.`);
      }
      
      fetchFacultyList(); 
      setShowUserForm(false);

    } catch (error) { 
        console.error("❌ Error saving user:", error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        alert(`Error saving user: ${error.message}`);
    }
    
    setIsSavingUser(false);
  };

  const handleSetUserStatus = async (compositeUserId, userName, newStatus) => {
    const confirmMsg = newStatus === 'active' ? `Are you sure you want to REINSTATE user ${userName}?` : `Are you sure you want to set user ${userName} to INACTIVE?`;
    if (!window.confirm(confirmMsg)) return;
    setIsSavingUser(true); setUploadStatus('');
    try {
      await updateDoc(doc(db, "faculty_staff", compositeUserId), { status: newStatus });
      setUploadStatus(`User ${userName}'s status has been set to '${newStatus}'.`);
      fetchFacultyList();
    } catch (error) { console.error(`Error updating status for ${userName}:`, error); setUploadStatus(`Error: ${error.message}`);}
    setIsSavingUser(false);
  };

  const handleDeleteUserPermanently = async (compositeUserId, userName) => {
    if (!window.confirm(`This will PERMANENTLY DELETE the user ${userName} (ID: ${compositeUserId}). This action CANNOT be undone. Are you sure?`)) return;
    setIsSavingUser(true); 
    setUploadStatus('');
    try {
      await deleteDoc(doc(db, "faculty_staff", compositeUserId));
      setUploadStatus(`User ${userName} has been permanently deleted.`);
      fetchFacultyList();
    } catch (error) { 
      console.error("Error permanently deleting user:", error); 
      setUploadStatus(`Error: ${error.message}`);
    }
    setIsSavingUser(false);
  };

  const fetchAllSubRequests = useCallback(async () => {
    setIsLoadingSubRequests(true);
    setUploadStatus('');
    try {
      let q_subs = collection(db, "sub_requests");
      if (currentUser && currentUser.tenantId) {
          q_subs = query(q_subs, where("tenantId", "==", currentUser.tenantId), orderBy("date", "desc"));
      } else if (currentUser?.isSystemAdmin && !currentUser.tenantId) {
          q_subs = query(q_subs, orderBy("date", "desc"));
      } else if (!currentUser) {
          setIsLoadingSubRequests(false);
          return;
      }
      const snapshot = await getDocs(q_subs);
      const requests = snapshot.docs.map(d => {
        const data = d.data();
        let dateObj = null;
        if (data.date?.toDate) {
            dateObj = data.date.toDate();
        } else if (typeof data.date === 'string') {
            dateObj = parseISO(data.date);
            if (!isValidDate(dateObj)) dateObj = null;
        }
        return { docId: d.id, ...data, dateObj };
      });
      setAllSubRequests(requests);
      setFilteredSubRequests(requests);
    } catch (error) { console.error("Error fetching sub requests:", error); setUploadStatus(`Error: ${error.message}`);}
    setIsLoadingSubRequests(false);
  }, [currentUser]);

  useEffect(() => {
    if (currentUser?.isSystemAdmin || currentUser?.isEducationalAdmin) {
      fetchAllSubRequests();
    }
  }, [currentUser, fetchAllSubRequests]);

  useEffect(() => {
    let tempRequests = [...allSubRequests];
    try {
        if (subRequestFilters.dateFrom) {
            const from = parseISO(subRequestFilters.dateFrom);
            if(isValidDate(from)) tempRequests = tempRequests.filter(r => r.dateObj && !isBefore(r.dateObj, from));
        }
        if (subRequestFilters.dateTo) {
            const to = parseISO(subRequestFilters.dateTo);
            if(isValidDate(to)) tempRequests = tempRequests.filter(r => r.dateObj && !isAfter(r.dateObj, to));
        }
    } catch (e) { console.error("Date filter error:", e); }
    if (subRequestFilters.teacherName) {
      tempRequests = tempRequests.filter(r => r.name?.toLowerCase().includes(subRequestFilters.teacherName.toLowerCase()));
    }
    if (subRequestFilters.department) {
      tempRequests = tempRequests.filter(r => r.department === subRequestFilters.department);
    }
    if (subRequestFilters.status !== 'all') {
      tempRequests = tempRequests.filter(req => {
        const hasFilledPeriod = (req.periods || []).some(p => req.filled?.[p]);
        if (subRequestFilters.status === 'filled') return hasFilledPeriod;
        if (subRequestFilters.status === 'unfilled') {
            const totalPeriods = (req.periods || []).length;
            if (totalPeriods === 0) return true; 
            const filledPeriodsCount = (req.periods || []).filter(p => req.filled?.[p]).length;
            return filledPeriodsCount < totalPeriods;
        }
        return true; 
      });
    }
    setFilteredSubRequests(tempRequests);
  }, [allSubRequests, subRequestFilters]);

  const handleSubRequestFilterChange = (e) => {
    setSubRequestFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEditSubRequest = (request) => {
      setEditingRequest(request);
      setUploadStatus('');
      setTimeout(() => scrollToRef(requestsSectionRef), 50);
  };

  const handleCancelEditSubRequest = () => {
      setEditingRequest(null);
  };

  const handleSaveSubRequest = async (requestId, updatedData) => {
      setIsSavingUser(true); 
      setUploadStatus('');
      try {
          const requestDocRef = doc(db, "sub_requests", requestId);
          await updateDoc(requestDocRef, updatedData);
          setUploadStatus(`Sub request ${requestId} updated successfully.`);
          setEditingRequest(null);
          fetchAllSubRequests(); 
      } catch (error) {
          console.error("Error updating sub request:", error);
          setUploadStatus(`Error updating sub request: ${error.message}`);
      }
      setIsSavingUser(false);
  };

  const handleCancelSubRequest = async (requestId, requestName, requestDateStr) => {
    if (!window.confirm(`Are you sure you want to CANCEL the sub request for ${requestName} on ${requestDateStr}?`)) return;
    setIsSavingUser(true); setUploadStatus('');
    try {
      await deleteDoc(doc(db, "sub_requests", requestId));
      setUploadStatus(`Sub request for ${requestName} was cancelled.`);
      fetchAllSubRequests();
    } catch (error) { console.error("Error cancelling request:", error); setUploadStatus(`Error: ${error.message}`);}
    setIsSavingUser(false);
  };

  const handleUnfillPeriod = async (requestId, periodToUnfill, teacherName, requestDate) => {
    if (!window.confirm(`Unfill Period ${periodToUnfill} for ${teacherName} on ${requestDate}? This will make the period available again.`)) {
        return;
    }
    setIsSavingUser(true); 
    setUploadStatus('');
    try {
        const requestDocRef = doc(db, "sub_requests", requestId);
        await updateDoc(requestDocRef, { [`filled.${periodToUnfill}`]: deleteField() });
        setUploadStatus(`Period ${periodToUnfill} for request by ${teacherName} has been unfilled.`);
        fetchAllSubRequests(); 
    } catch (error) {
        console.error("Error unfilling period:", error);
        setUploadStatus(`Error unfilling period: ${error.message}`);
    }
    setIsSavingUser(false);
  };

  if (!(currentUser?.isSystemAdmin || currentUser?.isEducationalAdmin)) {
    return <p>You are not authorized to view this page.</p>;
  }

  const uniqueDepartmentsForFilter = [...new Set(allFaculty.map(f => f.department).filter(dep => dep && dep !== 'Outside Sub'))].sort();
  
  // Get available secondary roles (exclude primary role)
  const availableSecondaryRoles = availableRoles.filter(role => 
    role !== userForm.role && !userForm.secondaryRoles.includes(role)
  );

  return (
    <div style={{ padding: '40px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ color: '#1976d2', marginBottom: '10px', fontSize: '32px' }}>⚙️ Admin Console</h1>
        <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
          Manage users, import data, and configure system settings for your organization.
        </p>
        
        <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
    <button onClick={() => scrollToRef(usersSectionRef)} 
            style={{ padding: '12px 20px', marginRight: '15px', fontSize: '16px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} 
            disabled={isProcessingCSV || isSavingUser}>
                👥 Manage Users
            </button>
            <button onClick={() => scrollToRef(requestsSectionRef)} 
                    style={{ padding: '12px 20px', marginRight: '15px', fontSize: '16px', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} 
                    disabled={isProcessingCSV || isSavingUser}>
                📋 Manage Sub Requests
            </button>
            <Link to="/admin/substitute-setup" 
                  style={{ padding: '12px 20px', fontSize: '16px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' }}>
                ⚙️ Sub Registration Setup
            </Link>
        </div>

        {currentUser?.isSystemAdmin && (
          <section style={{ marginBottom: '30px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderBottom: '1px solid #ddd' }}>
              <h2 style={{ margin: 0, color: '#1976d2', display: 'flex', alignItems: 'center', fontSize: '24px' }}>
                📤 Upload Faculty/Staff CSV
              </h2>
              <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                Import multiple users at once using a CSV file. Download the template to get started.
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <button type="button" onClick={handleDownloadTemplate} 
                        style={{ padding: '10px 16px', marginRight: '15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} 
                        disabled={isProcessingCSV || isSavingUser}>
                  📥 Download CSV Template
                </button>
                <span style={{fontSize: '14px', color: '#666'}}>({TEMPLATE_FILE_NAME})</span>
              </div>
              <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                
                <p style={{marginTop: 0, fontSize: '0.9em', color: '#555'}}>
                  <strong>Required fields:</strong> sis_id, firstName, lastName, email, role, division<br/>
                  <strong>Optional fields:</strong> department, secondaryDepartment, mobileNumber, title, status, supervisor_sis_id<br/>
                  <br/>
                  <strong>Role values:</strong> Faculty/Staff, HR, System Administrator, Educational Administrator, Outside Sub, Volunteer Manager, Volunteer<br/>
                  <strong>Division values:</strong> EC <span style={{color: '#999', fontStyle: 'italic'}}>(Early Childhood)</span>, LS <span style={{color: '#999', fontStyle: 'italic'}}>(Lower School)</span>, MS <span style={{color: '#999', fontStyle: 'italic'}}>(Middle School)</span>, HS <span style={{color: '#999', fontStyle: 'italic'}}>(High School) - custom divisions can be defined in the System Setup before import</span><br/>
                  <strong>Mobile Number:</strong> 10-digit US mobile phone for SMS (e.g., 5551234567)<br/>
                  <strong>Status values:</strong> active, inactive <span style={{color: '#999', fontStyle: 'italic'}}>(default: users typically enter as active)</span><br/>
                  <br/>
                  <i>The `supervisor_sis_id` must correspond to another user's `sis_id` in the database or the same upload file.</i>
                </p>
              </div>
              <input type="file" accept=".csv" onChange={handleFileChange} disabled={isProcessingCSV || isSavingUser} 
                     style={{marginBottom: '15px', display: 'block', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', width: '300px'}}/>
              <button onClick={handleUpload} disabled={isProcessingCSV || !csvFile || isSavingUser} 
                      style={{ padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                {isProcessingCSV ? 'Uploading...' : '⬆️ Upload & Process CSV'}
              </button>
              {uploadStatus && (
                <div style={{ marginTop: '20px', padding: '15px', borderRadius: '4px', 
                            backgroundColor: (errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#f8d7da' : '#d4edda',
                            color: (errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#721c24' : '#155724',
                            border: `1px solid ${(errorCount > 0 || uploadStatus.toLowerCase().includes('error')) ? '#f5c6cb' : '#c3e6cb'}` }}>
                  <div dangerouslySetInnerHTML={{ __html: uploadStatus }} />
                </div>
              )}
              {errorCount > 0 && errorMessages.length > 0 && (
                <div style={{ marginTop: '15px', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px', padding: '15px' }}>
                  <p style={{ margin: '0 0 10px 0', fontWeight: '500', color: '#721c24' }}>Error Details (showing first 10):</p>
                  <ul style={{ margin: 0, paddingLeft: '20px', color: '#721c24' }}>
                    {errorMessages.slice(0,10).map((msg,idx) => <li key={idx} style={{fontSize:'13px', marginBottom: '5px'}}>{msg}</li>)}
                    {errorMessages.length > 10 && <li style={{fontStyle: 'italic'}}>...and {errorMessages.length - 10} more.</li>}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {currentUser?.isSystemAdmin && ( 
          <section ref={usersSectionRef} style={{ marginBottom: '30px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderBottom: '1px solid #ddd' }}>
              <h2 style={{ margin: 0, color: '#1976d2', display: 'flex', alignItems: 'center', fontSize: '24px' }}>
                👥 Manage Faculty/Staff
              </h2>
              <p style={{ margin: '8px 0 0 0', color: '#666' }}>
                Add, edit, and manage user accounts. Configure roles and permissions for your organization.
              </p>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                <button onClick={openAddUserForm} 
                        style={{ padding: '12px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }} 
                        disabled={isProcessingCSV || isSavingUser}>
                  ➕ Add New User (Manual)
                </button>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <label htmlFor="user-view-filter" style={{marginRight: '10px', fontWeight: '500'}}>View:</label>
                  <select id="user-view-filter" value={userViewFilter} onChange={(e) => setUserViewFilter(e.target.value)} 
                          style={{padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px'}}>
                    <option value="active">Active Users</option> 
                    <option value="inactive">Inactive Users</option> 
                    <option value="all">All Users</option>
                  </select>
                </div>
              </div>
              
              {showUserForm && (
                <div ref={userEditFormRef} style={{ border: '1px solid #ddd', padding: '25px', marginBottom: '25px', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                  <h3 style={{ marginTop: 0, color: '#1976d2', fontSize: '20px' }}>
                    {formMode === 'add' ? '➕ Add New User' : `✏️ Edit User: ${userForm.name || `${userForm.firstName} ${userForm.lastName}`}`}
                  </h3>
                  <form onSubmit={handleUserFormSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label htmlFor="status_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Status:</label>
                        <select id="status_form" name="status" value={userForm.status} onChange={handleUserFormChange} 
                                style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="sis_id_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>SIS ID*:</label>
                        <input type="text" id="sis_id_form" name="sis_id" value={userForm.sis_id} onChange={handleUserFormChange} required disabled={formMode==='edit'} 
                               style={formMode === 'edit' ? 
                                 {backgroundColor: '#e9ecef', width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'} : 
                                 {width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}/>
                        {formMode === 'edit' && <small style={{display: 'block', color: '#6c757d', marginTop: '5px'}}>SIS ID cannot be changed after creation.</small>}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label htmlFor="firstName_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>First Name*:</label>
                        <input type="text" id="firstName_form" name="firstName" value={userForm.firstName} onChange={handleUserFormChange} required 
                               style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
                      </div>
                      <div>
                        <label htmlFor="lastName_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Last Name*:</label>
                        <input type="text" id="lastName_form" name="lastName" value={userForm.lastName} onChange={handleUserFormChange} required 
                               style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
                      </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label htmlFor="email_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Email*:</label>
                      <input type="email" id="email_form" name="email" value={userForm.email} onChange={handleUserFormChange} required 
                             style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label htmlFor="role_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Primary Role*:</label>
                        <select id="role_form" name="role" value={userForm.role} onChange={handleUserFormChange} required 
                                style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}>
                          <option value="">-- Select Primary Role --</option>
                          {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="department_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Department*:</label>
                        <input type="text" id="department_form" name="department" value={userForm.department} onChange={handleUserFormChange} required 
                               style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
                      </div>
                    </div>

                    {/* NEW: Division field */}
                    <div style={{ marginBottom: '20px' }}>
                      <label htmlFor="division_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Division*:</label>
                      <select id="division_form" name="division" value={userForm.division} onChange={handleUserFormChange} required 
                              style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}>
                        <option value="">-- Select Division --</option>
                        {divisions.map(div => (
                          <option key={div.code} value={div.code}>{div.label} ({div.code})</option>
                        ))}
                      </select>
                    </div>

                    {/* Secondary Department - Optional */}
                    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px' }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#f57c00', fontSize: '16px' }}>
                        📚 Secondary Department (Optional)
                      </h4>
                      <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#666' }}>
                        Select if this user teaches in multiple departments (e.g., Math and Science)
                      </p>
                      <label htmlFor="secondaryDepartment_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Secondary Department:
                      </label>
                      <select 
                        id="secondaryDepartment_form" 
                        name="secondaryDepartment" 
                        value={userForm.secondaryDepartment} 
                        onChange={handleUserFormChange}
                        style={{
                          width: '100%', 
                          padding: '10px', 
                          border: '1px solid #ddd', 
                          borderRadius: '4px', 
                          boxSizing: 'border-box'
                        }}
                      >
                        <option value="">-- None --</option>
                        {availableAcademicDepartments.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                      {userForm.secondaryDepartment && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#28a745', fontWeight: '500' }}>
                          ✓ Will appear in both {userForm.department} and {userForm.secondaryDepartment} department sharing
                        </p>
                      )}
                    </div>

                    {/* Secondary Roles Section */}
                    <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#e8f4fd', border: '1px solid #bee5eb', borderRadius: '8px' }}>
                      <h4 style={{ margin: '0 0 15px 0', color: '#0c5460', fontSize: '16px' }}>🔄 Secondary Roles (Role Switching)</h4>
                      <p style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#0c5460' }}>
                        Add additional roles that this user can switch between. Users will see a role switcher in the sidebar if they have multiple roles.
                      </p>
                      
                      {userForm.secondaryRoles.length > 0 && (
                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Current Secondary Roles:</label>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {userForm.secondaryRoles.map(role => (
                              <span key={role} style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                backgroundColor: '#007bff', 
                                color: 'white', 
                                padding: '6px 12px', 
                                borderRadius: '16px', 
                                fontSize: '14px' 
                              }}>
                                {role}
                                <button
                                  type="button"
                                  onClick={() => removeSecondaryRole(role)}
                                  style={{ 
                                    marginLeft: '8px', 
                                    background: 'none', 
                                    border: 'none', 
                                    color: 'white', 
                                    cursor: 'pointer', 
                                    fontSize: '16px',
                                    padding: '0',
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  title="Remove role"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {availableSecondaryRoles.length > 0 && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Add Secondary Role:</label>
                          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <select 
                              value="" 
                              onChange={(e) => {
                                if (e.target.value) {
                                  addSecondaryRole(e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
                            >
                              <option value="">-- Select Role to Add --</option>
                              {availableSecondaryRoles.map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      {userForm.role && availableSecondaryRoles.length === 0 && userForm.secondaryRoles.length === 0 && (
                        <p style={{ margin: 0, fontSize: '14px', color: '#6c757d', fontStyle: 'italic' }}>
                          No additional roles available. User will have single-role access only.
                        </p>
                      )}
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label htmlFor="supervisorId_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Supervisor:</label>
                      <select
                          id="supervisorId_form"
                          name="supervisorId"
                          value={userForm.supervisorId || ''}
                          onChange={handleUserFormChange}
                          style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}
                      >
                          <option value="">-- No Supervisor --</option>
                          {allFaculty
                              .filter(faculty => faculty.docId !== userForm.docId)
                              .map(faculty => (
                                  <option key={faculty.docId} value={faculty.docId}>{faculty.name} ({faculty.sis_id})</option>
                              ))
                          }
                      </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                      <div>
                        <label htmlFor="title_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Title:</label>
                        <input type="text" id="title_form" name="title" value={userForm.title} onChange={handleUserFormChange} 
                              style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
                      </div>
                      <div>
                        <label htmlFor="mobileNumber_form" style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                          Mobile Number (SMS) 📱
                        </label>
                        <input 
                          type="tel" 
                          id="mobileNumber_form" 
                          name="mobileNumber" 
                          value={userForm.mobileNumber ? userForm.mobileNumber.replace('+1', '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : ''} 
                          onChange={(e) => {
                            // Format as user types: (555) 555-5555
                            const numbers = e.target.value.replace(/\D/g, '');
                            let formatted = '';
                            if (numbers.length <= 3) {
                              formatted = numbers;
                            } else if (numbers.length <= 6) {
                              formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
                            } else {
                              formatted = `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
                            }
                            // Convert to E.164 for storage
                            const cleanNumbers = numbers.slice(0, 10);
                            const e164 = cleanNumbers.length === 10 ? `+1${cleanNumbers}` : '';
                            setUserForm(prev => ({ ...prev, mobileNumber: e164 }));
                          }}
                          placeholder="(555) 555-5555"
                          maxLength={14}
                          style={{width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} 
                        />
                        <small style={{ display: 'block', color: '#666', marginTop: '5px', fontSize: '12px' }}>
                          Required for SMS notifications. Must be a mobile number (not landline).
                        </small>
                        {userForm.phone && userForm.phone !== userForm.mobileNumber && (
                          <small style={{ display: 'block', color: '#856404', marginTop: '5px', fontSize: '12px', fontStyle: 'italic' }}>
                            Legacy phone: {userForm.phone}
                          </small>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '15px' }}>
                      <button type="submit" disabled={isProcessingCSV || isSavingUser} 
                              style={{ padding: '12px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                        {isSavingUser ? 'Saving...' : (formMode === 'add' ? 'Add User' : 'Update User')}
                      </button>
                      <button type="button" onClick={() => setShowUserForm(false)} disabled={isProcessingCSV || isSavingUser} 
                              style={{padding: '12px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px'}}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
              
              {isLoadingUsers ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading users...</div>
              ) : ( 
                filteredFacultyList.length > 0 ? (
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    overflowX: 'auto', 
                    maxWidth: '100%'
                  }}>
                    <table style={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      minWidth: '1200px' 
                    }}>
                      <thead>
                        <tr style={{backgroundColor: '#f8f9fa'}}>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '80px'}}>SIS ID</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '150px'}}>Name</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '200px'}}>Email</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '120px'}}>Department</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '50px'}}>Division</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '140px', maxWidth: '200px', width: '200px'}}>Roles</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '120px'}}>Supervisor</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '80px'}}>Status</th>
                          <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600', minWidth: '200px', position: 'sticky', right: 0, backgroundColor: '#f8f9fa', zIndex: 1}}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredFacultyList.map(user => {
                          const supervisor = allFaculty.find(f => f.docId === user.supervisorId);
                          const getDivisionColor = (division) => {
                            const colors = {
                              EC: '#ff9800', // Orange
                              LS: '#4caf50', // Green  
                              MS: '#2196f3', // Blue
                              HS: '#9c27b0'  // Purple
                            };
                            return colors[division] || '#757575';
                          };
                          
                          // Format mobile number for display
                          const formatMobileDisplay = (mobileNumber) => {
                            if (!mobileNumber) return '';
                            const cleaned = mobileNumber.replace(/\D/g, '');
                            if (cleaned.length === 11 && cleaned.startsWith('1')) {
                              const number = cleaned.substring(1);
                              return `(${number.slice(0, 3)}) ${number.slice(3, 6)}-${number.slice(6)}`;
                            } else if (cleaned.length === 10) {
                              return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
                            }
                            return mobileNumber;
                          };
                          
                          return (
                            <tr key={user.docId} style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{border: '1px solid #ddd', padding: '12px'}}>{user.sis_id}</td>
                              <td style={{border: '1px solid #ddd', padding: '12px', fontWeight: '500'}}>{user.name}</td>
                              <td style={{border: '1px solid #ddd', padding: '12px', fontSize: '14px'}}>{user.email}</td>
                              <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                {user.department}
                                {user.secondaryDepartment && (
                                  <span style={{ 
                                    marginLeft: '6px', 
                                    padding: '2px 6px', 
                                    backgroundColor: '#fff3cd', 
                                    color: '#856404',
                                    borderRadius: '3px', 
                                    fontSize: '11px',
                                    fontWeight: '500'
                                  }}>
                                    +{user.secondaryDepartment}
                                  </span>
                                )}
                              </td>
                              <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                <span style={{ 
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  backgroundColor: getDivisionColor(user.division || 'LS'),
                                  color: 'white',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  {user.division || 'LS'}
                                </span>
                              </td>
                              <td style={{border: '1px solid #ddd', padding: '12px', whiteSpace: 'normal', wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '200px', width: '200px'}}>
                                <div>
                                  <span style={{ fontWeight: '600', color: '#1976d2', fontSize: '14px' }}>{user.role}</span>
                                  {user.secondaryRoles && user.secondaryRoles.length > 0 && (
                                    <div style={{ marginTop: '4px' }}>
                                      {user.secondaryRoles.map(role => (
                                        <span key={role} style={{ 
                                          display: 'inline-block', 
                                          backgroundColor: '#e3f2fd', 
                                          color: '#1976d2', 
                                          padding: '2px 6px', 
                                          borderRadius: '12px', 
                                          fontSize: '11px', 
                                          marginRight: '4px',
                                          marginTop: '2px'
                                        }}>
                                          {role}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td style={{border: '1px solid #ddd', padding: '12px', fontSize: '14px'}}>{supervisor?.name || <span style={{color: '#999'}}>N/A</span>}</td>
                              <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                <span style={{ 
                                  padding: '4px 8px', 
                                  borderRadius: '12px', 
                                  fontSize: '12px', 
                                  fontWeight: '500',
                                  backgroundColor: (user.status || 'active') === 'active' ? '#d4edda' : '#f8d7da',
                                  color: (user.status || 'active') === 'active' ? '#155724' : '#721c24'
                                }}>
                                  {user.status || 'active'}
                                </span>
                              </td>
                              <td style={{
                                border: '1px solid #ddd', 
                                padding: '12px',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: 'white',
                                zIndex: 1,
                                boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', minWidth: '180px' }}>
                                  <button onClick={() => openEditUserForm(user)} 
                                          style={{ 
                                            padding: '4px 8px', 
                                            fontSize: '11px', 
                                            backgroundColor: '#007bff', 
                                            color: 'white', 
                                            border: 'none', 
                                            borderRadius: '3px', 
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap'
                                          }} 
                                          disabled={isProcessingCSV || isSavingUser}>
                                    Edit
                                  </button>
                                  {(user.status === 'inactive') ? (
                                    <>
                                      <button onClick={() => handleSetUserStatus(user.docId, user.name, 'active')} 
                                              style={{ 
                                                padding: '4px 8px', 
                                                fontSize: '11px', 
                                                backgroundColor: '#28a745', 
                                                color: 'white', 
                                                border: 'none', 
                                                borderRadius: '3px', 
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap'
                                              }} 
                                              disabled={isProcessingCSV || isSavingUser}>
                                        Reactivate
                                      </button>
                                      <button onClick={() => handleDeleteUserPermanently(user.docId, user.name)} 
                                              style={{ 
                                                padding: '4px 8px', 
                                                fontSize: '11px', 
                                                backgroundColor: '#dc3545', 
                                                color: 'white', 
                                                border: 'none', 
                                                borderRadius: '3px', 
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap'
                                              }} 
                                              disabled={isProcessingCSV || isSavingUser}>
                                        Delete
                                      </button>
                                    </>
                                  ) : (
                                    <button onClick={() => handleSetUserStatus(user.docId, user.name, 'inactive')} 
                                            style={{
                                              padding: '4px 8px', 
                                              fontSize: '11px', 
                                              backgroundColor: '#6c757d', 
                                              color: 'white', 
                                              border: 'none', 
                                              borderRadius: '3px', 
                                              cursor: 'pointer',
                                              whiteSpace: 'nowrap'
                                            }} 
                                            disabled={isProcessingCSV || isSavingUser}>
                                      Set Inactive
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    No users found for the current filter '{userViewFilter}'.
                  </div>
                )
              )}
            </div>
          </section>
        )}

        <section ref={requestsSectionRef} style={{ marginBottom: '30px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <div style={{ backgroundColor: '#e3f2fd', padding: '20px', borderBottom: '1px solid #ddd' }}>
            <h2 style={{ margin: 0, color: '#1976d2', display: 'flex', alignItems: 'center', fontSize: '24px' }}>
              📋 Manage Sub Requests
            </h2>
            <p style={{ margin: '8px 0 0 0', color: '#666' }}>
              View, edit, and manage substitute requests across your organization.
            </p>
          </div>
          <div style={{ padding: '20px' }}>
            {editingRequest && (
                <SubRequestEditForm
                    request={editingRequest}
                    onSave={handleSaveSubRequest}
                    onCancel={handleCancelEditSubRequest}
                    isSaving={isSavingUser}
                    facultyList={allFaculty.filter(u => (u.status || 'active') === 'active')}
                />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <div>
                <label htmlFor="filterDateFrom" style={{display:'block', marginBottom:'5px', fontWeight: '500'}}>Date From:</label>
                <input type="date" id="filterDateFrom" name="dateFrom" value={subRequestFilters.dateFrom} onChange={handleSubRequestFilterChange} 
                       style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
              </div>
              <div>
                <label htmlFor="filterDateTo" style={{display:'block', marginBottom:'5px', fontWeight: '500'}}>Date To:</label>
                <input type="date" id="filterDateTo" name="dateTo" value={subRequestFilters.dateTo} onChange={handleSubRequestFilterChange} 
                       style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
              </div>
              <div>
                <label htmlFor="filterTeacherName" style={{display:'block', marginBottom:'5px', fontWeight: '500'}}>Teacher Name:</label>
                <input type="text" id="filterTeacherName" name="teacherName" placeholder="Filter by teacher" value={subRequestFilters.teacherName} onChange={handleSubRequestFilterChange} 
                       style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}} />
              </div>
              <div>
                <label htmlFor="filterDepartment" style={{display:'block', marginBottom:'5px', fontWeight: '500'}}>Department:</label>
                <select id="filterDepartment" name="department" value={subRequestFilters.department} onChange={handleSubRequestFilterChange} 
                        style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}>
                  <option value="">All Departments</option>
                  {uniqueDepartmentsForFilter.map(dep => <option key={dep} value={dep}>{dep}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="filterStatus" style={{display:'block', marginBottom:'5px', fontWeight: '500'}}>Status:</label>
                <select id="filterStatus" name="status" value={subRequestFilters.status} onChange={handleSubRequestFilterChange} 
                        style={{width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', boxSizing: 'border-box'}}>
                  <option value="all">All</option>
                  <option value="unfilled">Unfilled</option>
                  <option value="filled">Filled</option>
                </select>
              </div>
              <div style={{display: 'flex', alignItems: 'end'}}>
                <button 
                  onClick={handleSyncSubJobs}
                  disabled={isProcessingCSV || isSavingUser}
                  style={{ 
                    padding: '8px 12px', 
                    backgroundColor: '#17a2b8', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    width: '100%'
                  }}
                >
                  🔄 Sync Sub Jobs
                </button>
              </div>
            </div>

            {isLoadingSubRequests ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading sub requests...</div>
            ) : ( 
              filteredSubRequests.length > 0 ? (
                <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{backgroundColor: '#f8f9fa'}}>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Date</th>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Teacher</th>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Dept.</th>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Periods (Filled By)</th>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Overall Status</th>
                        <th style={{border: '1px solid #ddd', padding: '12px', textAlign: 'left', fontWeight: '600'}}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubRequests.map(req => {
                          const isAnyPeriodFilled = (req.periods || []).some(p => req.filled?.[p]);
                          const areAllPeriodsFilled = (req.periods || []).length > 0 && (req.periods || []).every(p => req.filled?.[p]);
                          let displayOverallStatus = "Unfilled";
                          let statusColor = '#ffc107';
                          if (areAllPeriodsFilled) {
                            displayOverallStatus = "Fully Filled";
                            statusColor = '#28a745';
                          } else if (isAnyPeriodFilled) {
                            displayOverallStatus = "Partially Filled";
                            statusColor = '#fd7e14';
                          }
                          
                          let formattedDate = req.date || 'N/A';
                          if (req.dateObj && isValidDate(req.dateObj)) {
                              formattedDate = format(req.dateObj, 'MM/dd/yyyy');
                          }
                          
                          return (
                            <tr key={req.docId} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{border: '1px solid #ddd', padding: '12px'}}>{formattedDate}</td>
                                <td style={{border: '1px solid #ddd', padding: '12px', fontWeight: '500'}}>{req.name}</td>
                                <td style={{border: '1px solid #ddd', padding: '12px'}}>{req.department}</td>
                                <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                  {(req.periods || []).map(p => (
                                    <div key={`${req.docId}_${p}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '4px 0' }}>
                                      <span style={{ fontSize: '14px' }}>
                                        <strong>P{p}:</strong> {req.filled?.[p]?.name || (req.filled?.[p] && typeof req.filled?.[p] !== 'object' ? `Taken (Legacy)` : <span style={{color:'#dc3545', fontWeight: '500'}}>Needed</span>)}
                                      </span>
                                      {req.filled?.[p] && ( 
                                        <button
                                          onClick={() => handleUnfillPeriod(req.docId, p, req.name, formattedDate)}
                                          disabled={isSavingUser}
                                          style={{ 
                                            marginLeft: '10px', 
                                            padding: '4px 8px', 
                                            fontSize: '12px', 
                                            backgroundColor: '#ffc107', 
                                            color: '#212529', 
                                            border: 'none', 
                                            borderRadius: '3px', 
                                            cursor: 'pointer' 
                                          }}
                                        >
                                          Unfill
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </td>
                                <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                  <span style={{ 
                                    padding: '4px 8px', 
                                    borderRadius: '12px', 
                                    fontSize: '12px', 
                                    fontWeight: '500',
                                    backgroundColor: statusColor + '20',
                                    color: statusColor,
                                    border: `1px solid ${statusColor}40`
                                  }}>
                                    {displayOverallStatus}
                                  </span>
                                </td>
                                <td style={{border: '1px solid #ddd', padding: '12px'}}>
                                  <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={() => handleEditSubRequest(req)} 
                                            style={{ padding: '6px 10px', fontSize: '12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }} 
                                            disabled={isSavingUser || isProcessingCSV || !!editingRequest}>
                                      Edit
                                    </button>
                                    <button onClick={() => handleCancelSubRequest(req.docId, req.name, formattedDate)} 
                                            style={{padding: '6px 10px', fontSize: '12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}} 
                                            disabled={isSavingUser || isProcessingCSV}>
                                      Cancel
                                    </button>
                                  </div>
                                </td>
                            </tr>
                          );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  No sub requests match the current filters.
                </div>
              )
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Admin;